import { captureException } from '@sentry/browser';
import { createIAPComponent } from '@play-co/components/frontend';
import {
  analytics as analyticsModule,
  isInLineMiniApp,
} from '@play-co/gcinstant';
import { GCInstant } from 'src/lib/gcinstant';
import {
  getBrowserLocale,
  getUsableLocale,
  loadableLocales,
} from 'src/lib/i18n/i18n';
import { options } from 'src/replicant/iap/component/iap.options';
import { State } from 'src/replicant/State';
import StateObserver, { user } from 'src/StateObserver';
import {
  onProblematicPurchase,
  onPurchaseSuccess,
  onPurchaseFailure,
  onPurchaseCanceled,
  onRedirect,
} from './iap.ux';
import { track } from 'src/lib/analytics/utils';
import { hasValidSaveState } from 'src/replicant/match2/savestate/match2-savestate.getters';
import { orderV2iOS, orderV2Android } from './iap.ruleset';
import {
  hideLoadingAnimation,
  showLoadingAnimation,
} from 'src/game/animation/loading';

/**
 * Possible product IDs
 */
type ProductID = keyof (typeof options)['items'];

/**
 * Create API functions for IAP component
 */
const createAPI = createIAPComponent(options, {
  /**
   * Set user locale for purchase
   */
  userLocale: () => {
    const settingsLocale = StateObserver.getLocale();

    if (loadableLocales[settingsLocale]) {
      return settingsLocale;
    }

    return getUsableLocale(getBrowserLocale());
  },

  /**
   * Set open web as purchase platform
   */
  platform: () => (isInLineMiniApp() ? 'GCInstant' : 'WebPay'),

  /**
   * Big button for web pay
   */
  defaultWebPayPurchaseMethod: () => 'komoju_paypay', // Always use Komoju PayPay for big button

  /**
   * GCInstant options
   */
  GCInstantPurchaseProvider: () => 'line',

  /**
   * Set specific providers order for purchase methods depending on the platform
   * ordered according to unique usage - https://app.amplitude.com/analytics/playco/chart/nmw9355s
   */
  overrideWebPayPurchaseMethodsOrder: (availablePaymentMethods) => {
    // Filter out MerPay if not enabled
    const methodsToUse = [...availablePaymentMethods];

    // Define order of payment methods for the platform
    const orderArray =
      GCInstant.osType === 'WEB_IOS' ? orderV2iOS : orderV2Android;

    // Sort available payment methods according to the predefined order
    return [...methodsToUse].sort((a, b) => {
      const indexA = orderArray.indexOf(a);
      const indexB = orderArray.indexOf(b);

      // If method isn't in the predefined order, place it at the end
      const posA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
      const posB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;

      return posA - posB;
    });
  },

  /**
   * Report gems state from user state, we use it to show after and before inventory analytics
   */
  inventory: (state: State) => ({
    freeGems: state.inventory.premium.free,
    paidGems: state.inventory.premium.paid,
  }),

  /**
   * Override the return URL to remove any sensitive information
   */
  overrideReturnToGameUrl: (originUrl) => {
    const url = new URL(originUrl);
    const { searchParams } = url;

    searchParams.delete('rts');

    if (searchParams.has('payment-intent')) {
      searchParams.delete('payment-intent');
      searchParams.delete('hl');
    }

    if (searchParams.has('payload')) {
      searchParams.delete('payload');
    }

    if (searchParams.has('request_id')) {
      searchParams.delete('request_id');
    }

    if (searchParams.has('flow')) {
      searchParams.delete('flow');
    }

    if (searchParams.has('amp_device_id')) {
      searchParams.delete('amp_device_id');
    }

    return url.toString();
  },
});

/**
 * Get API functions for IAP component
 */
const api = () =>
  createAPI({
    replicant: StateObserver.replicant,
    analytics: analyticsModule,
    GCInstant: GCInstant,
  });

/**
 * Buy a product over  component API
 */
export async function buyProductNew(
  productId: ProductID,
  opts: {
    feature: string;
    subFeature: string;
    onComplete?: (success: boolean) => void;
    onBeforeReward?: () => Promise<void>;
  },
) {
  const state = StateObserver.getState();
  const now = StateObserver.now();
  const { buyProduct, isIapReady } = api();

  const analytics = {
    feature: opts.feature,
    subFeature: opts.subFeature,
    extra: {
      isCatalogLoaded: GCInstant.catalog.length > 0,
      saveStateAvailable: hasValidSaveState(state.user, now),
    },
    breadcrumb: state.ui.stack,
    shopWasShownTimestamp: state.analytics.purchaseShownTime,
  };

  // Show loading animation
  void showLoadingAnimation();

  // Ensure IAP is ready before proceeding
  await isIapReady();

  // Start purchase
  const result = await buyProduct(productId, analytics, {
    onSuccess: async (id, result) => {
      if (opts.onBeforeReward) {
        const { ui } = StateObserver.getState();
        if (ui.loading) hideLoadingAnimation();
        try {
          await opts.onBeforeReward();
        } catch (err) {
          captureException(err, { tags: { context: 'onBeforeReward' } });
        }
      }
      return onPurchaseSuccess(id, {
        feature: result.analytics.feature,
        subFeature: result.analytics.subFeature,
      });
    },
    onPending: async (id, result) =>
      onProblematicPurchase(id, {
        provider: result.purchaseProvider,
        method: result.purchaseMethod,
        purchaseID: result.pendingPayment.purchaseId,
        feature: result.analytics.feature,
        subFeature: result.analytics.subFeature,
      }),
    onError: async (_, result) =>
      onPurchaseFailure(productId, {
        type: 'failed',
        feature: result.analytics.feature,
        subFeature: result.analytics.subFeature,
      }),
    onCancel: async (_, result) =>
      onPurchaseCanceled(productId, {
        feature: result.analytics.feature,
        subFeature: result.analytics.subFeature,
      }),
    onRedirect: async (url, result) =>
      onRedirect(productId, {
        redirectUrl: url,
        provider: result.purchaseProvider,
        method: result.purchaseMethod,
        purchaseID: result.pendingPayment.purchaseId,
        feature: result.analytics.feature,
        subFeature: result.analytics.subFeature,
      }),
  }).finally(() => {
    const { ui } = StateObserver.getState();
    if (ui.loading) hideLoadingAnimation();
  });

  if (opts.onComplete) {
    opts.onComplete(result.status === 'success');
  }

  return result;
}

/**
 * Restore purchase over component API
 */
export function resolvePendingPurchases() {
  const { resolvePendingPurchases } = api();

  return resolvePendingPurchases({
    onSuccess: async ({ pendingPayment }) =>
      onPurchaseSuccess(pendingPayment.productId, {
        feature: pendingPayment.analytics.feature,
        subFeature: pendingPayment.analytics.subFeature,
      }),
    onError: async ({ pendingPayment }) => {
      track('DebugFailedToResolvePurchase', {
        provider: pendingPayment.purchaseProvider,
        method: pendingPayment.purchaseMethod,
        productID: pendingPayment.productId,
        purchaseID: pendingPayment.purchaseId,
        feature: pendingPayment.analytics.feature,
        subFeature: pendingPayment.analytics.subFeature,
      });
    },
    onCancel: async ({ pendingPayment }) => {
      track('DebugPendingPurchaseWasCancelled', {
        provider: pendingPayment.purchaseProvider,
        method: pendingPayment.purchaseMethod,
        productID: pendingPayment.productId,
        purchaseID: pendingPayment.purchaseId,
        feature: pendingPayment.analytics.feature,
        subFeature: pendingPayment.analytics.subFeature,
      });
    },
    onPending: async ({ pendingPayment }) => {
      track('DebugFailedToResolveProblematicPurchase', {
        provider: pendingPayment.purchaseProvider,
        method: pendingPayment.purchaseMethod,
        productID: pendingPayment.productId,
        purchaseID: pendingPayment.purchaseId,
        feature: pendingPayment.analytics.feature,
        subFeature: pendingPayment.analytics.subFeature,
      });
    },
  });
}

/**
 * Initialize IAP component
 * Not required for WebPay but required for LINE IAP
 * @returns {Promise<boolean>} - true if IAP is ready, false otherwise
 */
export async function initializeIAP() {
  const { initialize, isIapReady, isIapSupported } = api();

  if (!isIapSupported()) {
    console.warn('IAP is not supported on this platform');
    return false;
  }

  const result = await initialize();
  if (!result) {
    console.warn('Failed to initialize IAP');
    return false;
  }

  await isIapReady();

  return true;
}

/**
 * Get provider catalog with regional prices for line IAP
 *
 * @returns {Product[]} - List of available products
 */
export function getProductCatalog(provider: 'line') {
  const { getProductsCatalog } = api();
  return getProductsCatalog(provider);
}

/**
 * Return if Line IAP is available.
 * LINE IAP is available when running in LINE MiniApp and IAP is ready.
 * @deprecated Use {@link isPaymentAvailable} from `src/lib/iap/iap.util.ts` instead.
 * LINE-only. Used inconsistently for both "show UI?" and product logic.
 */
export function isLineIAPAvailable() {
  const { iap } = StateObserver.getState();

  if (iap.ready === false) {
    return false;
  }

  return isInLineMiniApp();
}
